import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { nsLayoutStyles } from '../../../ns-styles/index.js';
import NSJobComponent from '../job.js';
import JobSchema from '../../../models/job-schema.js';
import type { FieldValues, TypeUi } from '../../../models/company-data.js';
import { readJobData, readLinkedRecords, saveDataRecord, type JobData } from '../../../helpers/company-data.js';
import { jobReferenceOptions } from '../../../views/data/job-values.js';
import { dataFields, type DataField } from '../../../views/data/definition.js';
import '../../../ns-elements/ns-data-record-form.js';
import '../../../ns-elements/ns-loading.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/data-add-record.json';
import { jobSchema } from '../job-schema.js';

// Add a record to the owner's data set from the mapped fields, through the job's own access
@customElement(NSDataAddRecord.is)
export default class NSDataAddRecord extends NSJobComponent {
  static get is() {
    return 'ns-data-add-record';
  }

  @state()
  private __data?: JobData;

  @state()
  private __fields: DataField[] = [];

  @state()
  private __values: FieldValues = {};

  @state()
  private __error = '';

  // The records its references pick from
  @state()
  private __linked?: Awaited<ReturnType<typeof readLinkedRecords>>;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { added: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .content {
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-m);
        height: 100%;
      }

      .prompt {
        white-space: pre-wrap;
      }

      .error {
        color: var(--wa-color-danger-on-quiet);
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();
    try {
      this.__data = await readJobData(this._auth?.token, this.job.id);
      this.__fields = dataFields(this.__data.definition, {
        ...this.__data.ui,
        titleField: this.__data.ui.titleField ?? '',
      } as TypeUi);
      // Workflow studio publishes a mapped reference only with a linked set to pick from
      if (this.__fields.some((field) => field.kind === 'reference')) {
        this.__linked = await readLinkedRecords(this._auth?.token, this.job.id);
      }
    } catch (err) {
      this.__error = err instanceof Error ? err.message : 'Unable to open this job.';
    }
  }

  private async __add() {
    this.__error = '';
    try {
      this.loading('Adding the record');
      // A record already added by this job, when finishing it failed last time, is the one it hands on
      const added =
        this.__data?.records[0]?.id ??
        (await saveDataRecord(this._auth?.token, { jobId: this.job.id, fields: this.__values })).id;
      await this.outcome('added', { RECORD: added });
      this.loaded();
    } catch (err) {
      this.loaded();
      this.__error = err instanceof Error ? err.message : 'Unable to add the record.';
    }
  }

  renderActions() {
    if (!this.__data) return nothing;
    return html`
      <wa-button variant="brand" type="button" @click=${() => this.__add()}>Add and finish</wa-button>
    `;
  }

  render() {
    const prompt = this.job.inputValues?.PROMPT;
    if (!this.__data) {
      return this.__error
        ? html`
            <p class="error" role="alert">${this.__error}</p>
          `
        : html`
            <ns-loading label="Loading..."></ns-loading>
          `;
    }
    return html`
      <div class="content">
        ${typeof prompt === 'string' && prompt
          ? html`
              <p class="prompt">${prompt}</p>
            `
          : nothing}
        <ns-data-record-form
          .fields=${this.__fields}
          .values=${this.__values}
          .referenceOptions=${jobReferenceOptions(this.__fields, this.__linked, this.__values, new Map())}
          @values-change=${(e: CustomEvent<{ values: FieldValues }>) => (this.__values = e.detail.values)}
        ></ns-data-record-form>
        ${this.__error
          ? html`
              <p class="error" role="alert">${this.__error}</p>
            `
          : nothing}
      </div>
    `;
  }
}
